import { useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { DefectStatus, PartLotSummaryDto } from '@bhops/shared';
import { canEditMfg } from '@/permissions';
import { partsApi } from '@/api/parts';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { kstYmd } from '@/lib/format-date';
import { Button } from '@/components/Form';
import { useDebounce } from '@/hooks/useDebounce';
import { PartLotCreateModal } from './PartLotCreateModal';

// docs/plan/v0.4.0-parts-solution-defect-lot/plan.md §6.2 — LOT 리스트 페이지.
// 필터: defectStatus / partCatalogId / 검색.
// 행 클릭 → /parts/lots/:id (영향권 페이지).

const DEFECT_LABEL: Record<DefectStatus, string> = {
  NORMAL: '정상',
  SUSPECTED: '의심',
  CONFIRMED: '확정',
  CLEARED: '해제',
};

const DEFECT_COLOR: Record<DefectStatus, { fg: string; bg: string; border: string }> = {
  NORMAL: { fg: 'var(--fg-muted)', bg: 'transparent', border: 'var(--border-default)' },
  SUSPECTED: {
    fg: 'var(--severity-warning)',
    bg: 'var(--bg-warning-subtle)',
    border: 'var(--severity-warning)',
  },
  CONFIRMED: {
    fg: 'var(--fg-danger)',
    bg: 'var(--bg-danger-subtle)',
    border: 'var(--fg-danger)',
  },
  CLEARED: {
    fg: 'var(--green-700)',
    bg: 'transparent',
    border: 'var(--green-700)',
  },
};

export function PartLotsPage() {
  const role = useAuthStore((s) => s.user?.role);
  // 계획서(2026-08-20) 요구 1 — 서버 parts.controller 의 LOT 입고 @Roles 와 같은
  // 집합(ADMIN·MANAGER·MFG_MANAGER)을 인라인 복사로 들고 있었다. 화면 쓰기 게이트
  // 정본은 canEditMfg 하나 — 복사가 남으면 서버가 바뀔 때 이 자리만 어긋난다.
  const canCreate = role != null && canEditMfg(role);
  const [defectFilter, setDefectFilter] = useState<DefectStatus | ''>('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [createOpen, setCreateOpen] = useState(false);

  const q = useQuery({
    queryKey: ['part-lots', { defectStatus: defectFilter || undefined, search: debouncedSearch }],
    queryFn: () =>
      partsApi.listLots({
        defectStatus: defectFilter || undefined,
        search: debouncedSearch || undefined,
      }),
    staleTime: 30_000,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
      >
        <div>
          {/* 오너 확정 2026-08-20 결정 4 — 사이드바 메뉴·라우트 handle 이 '부품 입고'
              라는 이름으로 복원됐다. 한 화면 한 이름: 메뉴에서 '부품 입고' 를 눌렀는데
              '부품 LOT' 가 뜨면 다른 화면에 온 것처럼 읽힌다(계획서 용어 = 부품 입고).

              오너 결정 2026-08-28 로 그 사이드바 항목은 숨겨졌다(nav-hidden.ts).
              이름은 그대로 '부품 입고' 로 둔다 — 라우트 handle(빵부스러기)과 계획서
              용어가 여전히 그 이름이고, 메뉴가 돌아올 때 이름이 또 갈리면 안 된다. */}
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            부품 입고
          </h1>
        </div>
        {canCreate && (
          <Button type="button" onClick={() => setCreateOpen(true)} data-testid="lot-create-button">
            + LOT 입고
          </Button>
        )}
      </header>

      <PartLotCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />

      {/* Filter bar */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          padding: '8px 12px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
        }}
      >
        <input
          placeholder="LOT 코드 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={input}
        />
        <select
          value={defectFilter}
          onChange={(e) => setDefectFilter(e.target.value as DefectStatus | '')}
          style={input}
        >
          <option value="">전체 상태</option>
          {(['NORMAL', 'SUSPECTED', 'CONFIRMED', 'CLEARED'] as DefectStatus[]).map((s) => (
            <option key={s} value={s}>
              {DEFECT_LABEL[s]}
            </option>
          ))}
        </select>
        <span style={{ color: 'var(--fg-muted)', fontSize: 12, marginLeft: 'auto' }}>
          {q.isFetching ? '불러오는 중…' : `${q.data?.length ?? 0} 건`}
        </span>
      </div>

      {/* Table */}
      <div
        style={{
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          background: 'var(--bg-surface)',
          overflow: 'auto',
        }}
      >
        {q.isLoading ? (
          <div style={emptyBlock}>불러오는 중…</div>
        ) : q.error ? (
          <div style={{ ...emptyBlock, color: 'var(--fg-danger)' }}>{extractApiError(q.error)}</div>
        ) : !q.data || q.data.length === 0 ? (
          <div style={emptyBlock}>
            조건에 맞는 LOT가 없습니다. 위의 [+ LOT 입고]로 등록해 주세요.
          </div>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>LOT 코드</th>
                <th style={th}>부품</th>
                <th style={th}>공급사</th>
                <th style={{ ...th, textAlign: 'right' }}>입고 / 잔여</th>
                <th style={{ ...th, textAlign: 'right' }}>장착 / 활성</th>
                <th style={{ ...th, textAlign: 'center' }}>결함 상태</th>
                <th style={th}>입고일</th>
                <th style={{ ...th, textAlign: 'right' }}>액션</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((lot) => (
                <LotRow key={lot.id} lot={lot} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function LotRow({ lot }: { lot: PartLotSummaryDto }) {
  const color = DEFECT_COLOR[lot.defectStatus];
  return (
    <tr style={{ borderTop: '1px solid var(--border-subtle)' }}>
      <td style={{ ...tdMono, fontWeight: 600 }}>
        <Link to={`/parts/lots/${lot.id}`} style={{ color: 'var(--fg-brand)' }}>
          {lot.lotCode}
        </Link>
      </td>
      <td style={td}>
        <div>{lot.partCatalog.displayName}</div>
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', fontFamily: 'var(--font-mono)' }}>
          {lot.partCatalog.sku}
        </div>
      </td>
      <td style={td}>{lot.supplierName ?? '–'}</td>
      <td style={{ ...tdMono, textAlign: 'right' }}>
        {lot.receivedQty} / {lot.remainingQty}
      </td>
      <td style={{ ...tdMono, textAlign: 'right' }}>
        {lot.partsCount} / <strong>{lot.activePartsCount}</strong>
      </td>
      <td style={{ ...td, textAlign: 'center' }}>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 999,
            border: `1px solid ${color.border}`,
            background: color.bg,
            color: color.fg,
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {DEFECT_LABEL[lot.defectStatus]}
          {lot.evidenceCount > 0 && (
            <span style={{ marginLeft: 4, fontWeight: 400 }}>· {lot.evidenceCount}건</span>
          )}
        </span>
      </td>
      {/* 한국시간 통일 5차(26-08-14) — 로트 입고일은 `PartLot.receivedAt @default(now())`
          이고 화면·API 어디에도 이 값을 보내는 자리가 없다(parts.service.createLot 는
          받지도 않는다) = 언제나 **진짜 순간**. 게다가 같은 값을 로트 영향 화면
          (PartLotImpactPage)은 이미 kstYmd 로 보여 줘 **두 화면이 하루 달랐다**. */}
      <td style={{ ...tdMono, fontSize: 11 }}>{kstYmd(lot.receivedAt) ?? '-'}</td>
      <td style={{ ...td, textAlign: 'right' }}>
        <Link
          to={`/parts/lots/${lot.id}`}
          style={{
            padding: '4px 10px',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-btn)',
            fontSize: 11,
            color: 'var(--fg-default)',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          영향권 →
        </Link>
      </td>
    </tr>
  );
}

const input: CSSProperties = {
  height: 30,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  fontSize: 13,
  color: 'var(--fg-default)',
  minWidth: 140,
};
const table: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};
const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};
const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const tdMono: CSSProperties = { ...td, fontFamily: 'var(--font-mono)' };
const emptyBlock: CSSProperties = {
  padding: 40,
  textAlign: 'center',
  color: 'var(--fg-muted)',
  fontSize: 13,
};
